import Phaser from "phaser";
import { NINE_SLICE_BUTTON_EVENT, NineSliceButton, ScrollableList } from "@yjsgames/phaser-ui";
import type { TabContentRect, TabViewContract } from "@yjsgames/phaser-ui/tab-registry";
import type { TextDirection } from "@yjsgames/i18n";
import { getTextDirection, t } from "../i18n/i18n.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { FONT_FAMILY, PRIMARY_BUTTON_STYLE, TYPO } from "./uiTokens.js";
import { resolveRecordTabLayout, RECORD_REPLAY_BUTTON_H, RECORD_REPLAY_BUTTON_W } from "./recordTabLayout.js";
import type { RecordDialogueCardData, RecordTabData } from "./recordTabData.js";

interface RowObjects {
  readonly id: string;
  readonly frame: Phaser.GameObjects.Rectangle;
  readonly trigger: Phaser.GameObjects.Text;
  readonly title: Phaser.GameObjects.Text;
  readonly preview: Phaser.GameObjects.Text;
  readonly replayButton: NineSliceButton;
}

const RECORD_REPLAY_BUTTON_STYLE = {
  ...PRIMARY_BUTTON_STYLE,
  textStyle: {
    ...PRIMARY_BUTTON_STYLE.textStyle,
    fontSize: TYPO.caption,
  },
};

function setVisibleActive<T extends Phaser.GameObjects.GameObject & { setVisible: (visible: boolean) => T }>(
  obj: T,
  visible: boolean,
): void {
  obj.setVisible(visible);
  (obj as Phaser.GameObjects.GameObject & { setActive?: (value: boolean) => unknown }).setActive?.(visible);
}

export interface RecordTabViewOptions {
  readonly onReplay?: (dialogueId: string) => void;
}

export class RecordTabView implements TabViewContract<RecordTabData> {
  private readonly title: Phaser.GameObjects.Text;
  private readonly empty: Phaser.GameObjects.Text;
  private readonly scrollList: ScrollableList;
  private readonly rows: RowObjects[] = [];
  private readonly rect: { x: number; y: number; width: number };
  private visible = false;
  private dir: TextDirection = getTextDirection();

  constructor(
    private readonly scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    private readonly bottomY: number,
    private readonly opts: RecordTabViewOptions = {},
  ) {
    this.rect = { x, y, width };
    const L = resolveRecordTabLayout({ ...this.rect, bottomY: this.bottomY }, 0);
    this.title = scene.add
      .text(L.title.x, L.title.y, t("record.title"), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.label,
        color: PALETTE.textPrimary,
      })
      .setOrigin(0.5, 0)
      .setDepth(3)
      .setVisible(false);
    this.empty = scene.add
      .text(L.empty.x, L.empty.y, t("record.empty"), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.body,
        color: PALETTE.textSecondary,
        align: "center",
        wordWrap: { width: L.empty.width },
      })
      .setOrigin(0, 0)
      .setDepth(3)
      .setVisible(false);

    this.scrollList = new ScrollableList(scene, L.scroll.x, L.scroll.y, {
      width: L.scroll.width,
      height: L.scroll.height,
      barWidth: 5,
      barColor: PALETTE_NUM.scrollBar,
      trackColor: PALETTE_NUM.scrollTrack,
      trackAlpha: 0.65,
      barAlpha: 0.95,
      scrollSpeed: 36,
    });
    this.scrollList.setDepth(2);
    this.scrollList.setVisible(false);
    scene.add.existing(this.scrollList);
  }

  update(data: RecordTabData): void {
    this.rebuildRows(data.dialogues);
    this.applyRowData(data.dialogues);
    this.relayout(this.dir);
    this.setVisible(this.visible);
  }

  applyLanguage(): void {
    this.title.setText(t("record.title"));
    this.empty.setText(t("record.empty"));
    for (const row of this.rows) {
      row.replayButton.setLabel(t("record.replay"));
    }
  }

  relayout(dir: TextDirection): void {
    this.dir = dir;
    const L = resolveRecordTabLayout({ ...this.rect, bottomY: this.bottomY }, this.rows.length);
    this.title.setPosition(L.title.x, L.title.y);
    this.empty.setPosition(L.empty.x, L.empty.y).setWordWrapWidth(L.empty.width).setAlign("center");
    this.scrollList.resize(L.scroll.x, L.scroll.y, L.scroll.width, L.scroll.height);
    this.scrollList.setContentHeight(L.contentHeight);

    const positions: { obj: Phaser.GameObjects.GameObject; lx: number; ly: number }[] = [];
    this.rows.forEach((row, i) => {
      const R = L.rows[i];
      if (!R) return;
      row.frame.setSize(R.frame.width, R.frame.height);
      row.trigger.setPosition(R.trigger.x, R.trigger.y).setWordWrapWidth(R.trigger.width);
      row.title.setPosition(R.title.x, R.title.y).setWordWrapWidth(R.title.width);
      row.preview.setPosition(R.preview.x, R.preview.y).setWordWrapWidth(R.preview.width);
      row.replayButton.setPosition(
        R.replayButton.x + R.replayButton.width / 2,
        R.replayButton.y + R.replayButton.height / 2,
      );
      positions.push(
        { obj: row.frame, lx: R.frame.x, ly: R.frame.y },
        { obj: row.trigger, lx: R.trigger.x, ly: R.trigger.y },
        { obj: row.title, lx: R.title.x, ly: R.title.y },
        { obj: row.preview, lx: R.preview.x, ly: R.preview.y },
        { obj: row.replayButton, lx: R.replayButton.x + R.replayButton.width / 2, ly: R.replayButton.y + R.replayButton.height / 2 },
      );
    });
    this.scrollList.relayoutContent(positions);
  }

  resize(rect: TabContentRect): void {
    this.rect.x = rect.x;
    this.rect.y = rect.y;
    this.rect.width = rect.width;
    this.relayout(getTextDirection());
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    setVisibleActive(this.title, visible);
    setVisibleActive(this.empty, visible && this.rows.length === 0);
    this.scrollList.setVisible(visible && this.rows.length > 0);
  }

  private rebuildRows(dialogues: readonly RecordDialogueCardData[]): void {
    const sameRows =
      this.rows.length === dialogues.length && this.rows.every((row, i) => row.id === dialogues[i]?.dialogueId);
    if (sameRows) return;

    this.scrollList.clearContent();
    for (const row of this.rows) {
      row.frame.destroy();
      row.trigger.destroy();
      row.title.destroy();
      row.preview.destroy();
      row.replayButton.destroy();
    }
    this.rows.length = 0;

    const L = resolveRecordTabLayout({ ...this.rect, bottomY: this.bottomY }, dialogues.length);
    dialogues.forEach((dialogue, i) => {
      const R = L.rows[i]!;
      const frame = this.scene.add
        .rectangle(R.frame.x, R.frame.y, R.frame.width, R.frame.height, PALETTE_NUM.cellBg, 0.88)
        .setOrigin(0, 0)
        .setStrokeStyle(1, PALETTE_NUM.gridPathGlow, 0.3)
        .setDepth(1);
      const trigger = this.scene.add
        .text(R.trigger.x, R.trigger.y, "", {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.caption,
          color: PALETTE.accentAmber,
          wordWrap: { width: R.trigger.width },
        })
        .setOrigin(0, 0.5)
        .setDepth(3);
      const title = this.scene.add
        .text(R.title.x, R.title.y, "", {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.label,
          fontStyle: "bold",
          color: PALETTE.textPrimary,
          wordWrap: { width: R.title.width },
        })
        .setOrigin(0, 0.5)
        .setDepth(3);
      const preview = this.scene.add
        .text(R.preview.x, R.preview.y, "", {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.caption,
          color: PALETTE.textSecondary,
          wordWrap: { width: R.preview.width },
        })
        .setOrigin(0, 0.5)
        .setDepth(3);
      const replayButton = new NineSliceButton(
        this.scene,
        R.replayButton.x + R.replayButton.width / 2,
        R.replayButton.y + R.replayButton.height / 2,
        {
          style: RECORD_REPLAY_BUTTON_STYLE,
          label: t("record.replay"),
          widthOverride: RECORD_REPLAY_BUTTON_W,
          heightOverride: RECORD_REPLAY_BUTTON_H,
        },
      ).setDepth(4);
      replayButton.on(NINE_SLICE_BUTTON_EVENT.Click, () => this.opts.onReplay?.(dialogue.dialogueId));

      this.scrollList.addContent(frame, R.frame.x, R.frame.y);
      this.scrollList.addContent(trigger, R.trigger.x, R.trigger.y);
      this.scrollList.addContent(title, R.title.x, R.title.y);
      this.scrollList.addContent(preview, R.preview.x, R.preview.y);
      this.scrollList.addContent(
        replayButton,
        R.replayButton.x + R.replayButton.width / 2,
        R.replayButton.y + R.replayButton.height / 2,
      );
      this.rows.push({ id: dialogue.dialogueId, frame, trigger, title, preview, replayButton });
    });
    this.scrollList.setContentHeight(L.contentHeight);
  }

  private applyRowData(dialogues: readonly RecordDialogueCardData[]): void {
    dialogues.forEach((dialogue, i) => {
      const row = this.rows[i];
      if (!row) return;
      row.trigger.setText(dialogue.triggerLabel);
      row.title.setText(dialogue.title);
      row.preview.setText(dialogue.preview);
      row.replayButton.setLabel(t("record.replay"));
    });
  }
}
